<template>
  <div
    class="international-phone-input"
    role="group"
    aria-label="Telephone Entry"
  >
    <div class="d-flex align-center">
      <v-select
        v-if="enableInternational"
        v-model="internalSelectedCountry"
        :items="countryList"
        aria-label="Select your country code"
        role="combobox"
        :disabled="disabled"
        :item-title="item => item.text"
        :item-value="item => item.value"
        :prepend-icon="prependIcon ? 'fa-light fa-mobile-button' : ''"
        :max-width="maxWidth"
        label="Country"
        variant="underlined"
        class="mt-3 mr-2 text-center no-underline"
        color="primary"
        hide-details="auto"
      />
      <v-text-field
        v-model="internalLocalPhone"
        label="Telephone Number"
        aria-label="Telephone Number"
        role="textbox"
        name="telephone"
        autocomplete="off"
        max-width="auto"
        hide-details="auto"
        variant="underlined"
        class="mt-3 no-underline"
        :placeholder="placeholder"
        :disabled="disabled"
        :rules="phoneNumberRules"
        :required="required"
        :prepend-icon="
          prependIcon && !enableInternational ? 'fa-light fa-mobile-button' : ''
        "
        @input="handleInput"
        @blur="handleBlur"
      />
    </div>
    <div
      class="ml-14 pl-4 pt-1 text-size-12 mt-2 border-top-default"
      :class="{
        'text-error border-top-error': errorMessages && errorMessages.length,
        'text-darkgrey': hint && (persistentHint || !errorMessages.length)
      }"
    >
      <template v-if="errorMessages && errorMessages.length">
        <div v-for="(msg, i) in errorMessages" :key="i">{{ msg }}</div>
      </template>
      <template v-else-if="hint && (persistentHint || !errorMessages.length)">
        {{ hint }}
      </template>
    </div>
  </div>
</template>
<script>
// TODO: Part 2 - Add As-You-Type formatting and validation
import { parsePhoneNumber } from "awesome-phonenumber";

export default {
  name: "InternationalPhoneInput",
  props: {
    modelValue: {
      type: String,
      default: ""
    },
    disabled: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: ""
    },
    enableInternational: {
      type: Boolean,
      default: true
    },
    availableCountries: {
      type: [Array, Object],
      default: () => [
        { text: "🇺🇸", value: "US" },
        { text: "🇨🇦", value: "CA" },
        { text: "🇬🇧", value: "GB" },
        { text: "🇩🇪", value: "DE" },
        { text: "🇫🇷", value: "FR" }
      ]
    },
    errorMessages: {
      type: Array,
      default: () => []
    },
    prependIcon: {
      type: Boolean,
      default: false
    },
    hint: {
      type: String,
      default: ""
    },
    persistentHint: {
      type: Boolean,
      default: false
    },
    phoneNumberRules: {
      type: Array,
      default: () => []
    },
    maxWidth: {
      type: Number,
      default: 120
    },
    overrideInternalPhone: {
      type: String,
      default: ""
    },
    required: {
      type: Boolean,
      default: false
    }
  },
  emits: [
    "update:modelValue",
    "blur",
    "touch",
    "update:errorMessages",
    "update:selectedCountry"
  ],
  data() {
    return {
      internalSelectedCountry: "US",
      internalLocalPhone: ""
    };
  },
  computed: {
    countryList() {
      return Array.isArray(this.availableCountries)
        ? this.availableCountries
        : Object.values(this.availableCountries);
    }
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(newVal) {
        if (!newVal) {
          this.resetInternalState();
          return;
        }

        const pn = parsePhoneNumber(newVal);

        if (pn && pn.valid && pn.possibility === "is-possible") {
          const region = pn.regionCode;
          if (
            region &&
            this.enableInternational &&
            this.isCountryAvailable(region)
          ) {
            this.internalSelectedCountry = region;
          }
          this.internalLocalPhone = pn.number.international || "";
        } else {
          this.internalLocalPhone = newVal;
        }
      }
    },
    availableCountries(newVal) {
      if (
        newVal &&
        newVal.length > 0 &&
        this.enableInternational &&
        this.modelValue
      ) {
        const pn = parsePhoneNumber(this.modelValue);
        if (pn.valid) {
          const region = pn.regionCode || "US";
          const isRegionAvailable = newVal.some(
            country => country.value === region
          );
          this.internalSelectedCountry =
            this.enableInternational && isRegionAvailable ? region : "US";
        }
      }
    },
    internalSelectedCountry(newCountry) {
      if (this.enableInternational) {
        this.updatePhoneValue();
        this.$emit("update:selectedCountry", newCountry);
      }
    }
  },
  methods: {
    resetInternalState() {
      this.internalLocalPhone = "";
      if (this.enableInternational) this.internalSelectedCountry = "US";
    },
    isCountryAvailable(region) {
      return this.countryList.some(country => country.value === region);
    },
    handleInput() {
      this.emitTouch();
    },
    updatePhoneValue() {
      const rawValue = this.internalLocalPhone.replace(/\s+/g, "").trim();
      if (!rawValue) {
        this.$emit("update:modelValue", "");
        this.$emit(
          "update:errorMessages",
          this.required ? ["Phone number is required"] : []
        );
        return;
      }

      const region = this.enableInternational
        ? this.internalSelectedCountry
        : "US";
      let pn;

      if (rawValue.startsWith("+")) {
        pn = parsePhoneNumber(rawValue);
        if (pn?.valid && pn.regionCode !== region) {
          this.$emit("update:errorMessages", [
            "Phone number does not match selected country"
          ]);
          return;
        }
      }

      if (!pn) {
        pn = parsePhoneNumber(rawValue, { regionCode: region });
        if (!pn?.valid) pn = parsePhoneNumber("+" + rawValue);
      }

      if (pn && pn.valid && pn.possibility === "is-possible") {
        if (pn.regionCode !== region) {
          this.$emit("update:errorMessages", [
            "Phone number does not match selected country"
          ]);
          return;
        }
        if (this.internalLocalPhone !== pn.number.international)
          this.internalLocalPhone = pn.number.international;
        this.$emit("update:modelValue", pn.number.e164);
        this.$emit("update:errorMessages", []);
      } else {
        this.$emit("update:errorMessages", [
          "Please enter a valid phone number"
        ]);
      }
    },
    handleBlur() {
      this.updatePhoneValue();
      this.$emit("blur");
    },
    emitTouch() {
      this.$emit("touch", this.internalLocalPhone);
    }
  }
};
</script>
<style lang="scss" scoped>
:deep(.no-underline .v-field__outline) {
  display: none;
}

:deep(.no-underline.v-select .v-field__append-inner i.v-icon) {
  margin-left: 0;
}

:deep(.no-underline.v-select .v-field__field) {
  padding-left: rem(5px);
  padding-top: rem(3px);
}
</style>
